import { gql, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { track } from '../../analytics/track';
import { AnalyticsEvents } from '../../analytics/events';

/** One question and the ticked options; a candidate matches the question by having picked at least one of them. */
export type AnswerOptionSelection = { questionId: string; optionIds: string[] };

export type FilterQuestion = {
  id: string;
  title: string;
  text: string | null;
  answered: number;
  options: { id: string; label: string; count: number }[];
};

const ANSWER_FILTER_MENU_QUERY = gql`
  query AnswerFilterMenu($jobId: String, $language: String, $filter: ApplicationFilter) {
    job(jobId: $jobId) {
      id
      answerFilter(language: $language, filter: $filter) {
        available
        reason
        limit
        questions {
          id
          title
          text
          answered
          options {
            id
            label
            count
          }
        }
      }
    }
  }
`;

/** The candidate list's own filter (stage, hide passed), so the menu counts what that list shows. */
export type AnswerListFilter = { stage: { equals: number | null }; scoreGteThreshold: boolean };

/**
 * The job's filterable questions (most answered first), in the recruiter's app language, with counts for the
 * candidate list being viewed (listFilter). Nothing is fetched until enabled, so a job page only pays for the
 * closed_answers read when the filter is used.
 */
export function useAnswerFilterQuestions(jobId: string, enabled: boolean, listFilter: AnswerListFilter) {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage ?? 'en').split('-')[0];
  const { data, loading, error } = useQuery(ANSWER_FILTER_MENU_QUERY, {
    variables: { jobId, language, filter: listFilter },
    skip: !enabled,
  });
  const menu = data?.job?.answerFilter;
  const questions: FilterQuestion[] = menu?.available ? menu.questions : [];
  const status: string | null = loading
    ? t('swiper.page.answerFilter.loading')
    : error
      ? t('swiper.page.answerFilter.error')
      : menu && !menu.available
        ? t('swiper.page.answerFilter.tooLarge', { limit: menu.limit })
        : menu && questions.length === 0
          ? t('swiper.page.answerFilter.noQuestions')
          : null;
  const unavailable = menu && !menu.available ? { reason: String(menu.reason), limit: Number(menu.limit) } : null;
  return { questions, status, loading, unavailable };
}

export const ticksOf = (selected: AnswerOptionSelection[], questionId: string) =>
  selected.find((x) => x.questionId === questionId)?.optionIds ?? [];

/** The selection with a question's ticks replaced; a question without ticks is dropped, since it doesn't filter. */
export function withTicks(selected: AnswerOptionSelection[], questionId: string, optionIds: string[]) {
  if (optionIds.length === 0) return selected.filter((x) => x.questionId !== questionId);
  return selected.some((x) => x.questionId === questionId)
    ? selected.map((x) => (x.questionId === questionId ? { questionId, optionIds } : x))
    : [...selected, { questionId, optionIds }];
}

export const toggled = (ids: string[], id: string) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);

export function trackAnswerFilter(next: AnswerOptionSelection[]) {
  track(AnalyticsEvents.CANDIDATE_FILTER_APPLIED, {
    filter_type: 'answer',
    selected_count: next.reduce((sum, x) => sum + x.optionIds.length, 0),
    question_count: next.length,
  });
}

/** "Bristol" / "Bristol, Plymouth" / "Bristol +2": the ticked answers in a short line. */
export function summarize(question: FilterQuestion, optionIds: string[]) {
  const labels = question.options.filter((o) => optionIds.includes(o.id)).map((o) => o.label);
  return labels.length > 2 ? `${labels[0]} +${labels.length - 1}` : labels.join(', ');
}
